import * as React from "react";

import { useInputField } from "../../components/molecules/InputWrapper/InputContext";

import { Option } from "../../types/Option";

import { useDebounce } from "../useDebounce";
import { useVirtualize as useVirtualizeHook } from "../useVirtualize";

type UseSelectBaseParamsSingle<T> = {
	mode: "single";
	options?: Option<T>[];
	fetchOptions?: (searchTerm: string) => Promise<Option<T>[]>;
	value: T | null | undefined;
	onChange: (value: T | null) => void;
	onSearchChange?: (searchValue: string) => void;
	errored?: boolean;
	disabled?: boolean;
	selectionVariant?: "select" | "combobox";
	virtualizationConfig?: {
		itemHeight?: number;
		maxHeight?: number;
	};
	id?: string;
};

type UseSelectBaseParamsMultiple<T> = {
	mode: "multiple";
	options?: Option<T>[];
	fetchOptions?: (searchTerm: string) => Promise<Option<T>[]>;
	value: T[];
	onChange: (value: T[]) => void;
	onSearchChange?: (searchValue: string) => void;
	errored?: boolean;
	disabled?: boolean;
	selectionVariant?: "select" | "combobox";
	virtualizationConfig?: {
		itemHeight?: number;
		maxHeight?: number;
	};
	id?: string;
};

type UseSelectBaseParams<T> = UseSelectBaseParamsSingle<T> | UseSelectBaseParamsMultiple<T>;

type UseSelectBaseReturnSingle<T> = {
	mode: "single";
	inputId: string;
	hasError: boolean;
	isDisabled: boolean;
	isOpen: boolean;
	searchValue: string;
	isLoading: boolean;
	selectedOption: Option<T> | undefined;
	filteredOptions: Option<T>[];
	options: Option<T>[];
	triggerWidth: number | undefined;
	triggerRef: React.RefObject<HTMLDivElement | null>;
	searchInputRef: React.RefObject<HTMLInputElement | null>;
	parentRef: React.RefObject<HTMLDivElement | null>;
	virtualizer: ReturnType<typeof useVirtualizeHook<Option<T>>>["virtualizer"];
	containerHeight: number;
	focusedIndex: number;
	isKeyboardNavActive: boolean;
	handleKeyDown: (e: React.KeyboardEvent) => void;
	handleItemMouseEnter: (index: number) => void;
	handleItemMouseLeave: () => void;
	setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
	setSearchValue: (value: string) => void;
	handleOpenChange: (open: boolean) => void;
	handleItemSelect: (option: Option<T>) => void;
};

type UseSelectBaseReturnMultiple<T> = {
	mode: "multiple";
	inputId: string;
	hasError: boolean;
	isDisabled: boolean;
	isOpen: boolean;
	searchValue: string;
	isLoading: boolean;
	selectedOptions: Option<T>[];
	availableOptions: Option<T>[];
	filteredOptions: Option<T>[];
	options: Option<T>[];
	triggerWidth: number | undefined;
	triggerRef: React.RefObject<HTMLDivElement | null>;
	searchInputRef: React.RefObject<HTMLInputElement | null>;
	parentRef: React.RefObject<HTMLDivElement | null>;
	virtualizer: ReturnType<typeof useVirtualizeHook<Option<T>>>["virtualizer"];
	containerHeight: number;
	focusedIndex: number;
	isKeyboardNavActive: boolean;
	handleKeyDown: (e: React.KeyboardEvent) => void;
	handleItemMouseEnter: (index: number) => void;
	handleItemMouseLeave: () => void;
	setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
	setSearchValue: (value: string) => void;
	handleOpenChange: (open: boolean) => void;
	handleItemSelect: (option: Option<T>) => void;
};

type UseSelectBaseReturn<T> = UseSelectBaseReturnSingle<T> | UseSelectBaseReturnMultiple<T>;

export const useSelectBase = <T,>(params: UseSelectBaseParams<T>): UseSelectBaseReturn<T> => {
	const { id: contextId, error, disabled: contextDisabled } = useInputField();
	const generatedId = React.useId();
	const inputId = params.id || contextId || generatedId;

	const [isOpen, setIsOpen] = React.useState(false);
	const [searchValue, setSearchValue] = React.useState("");
	const [internalOptions, setInternalOptions] = React.useState<Option<T>[]>([]);
	const [isLoading, setIsLoading] = React.useState(false);
	const [selectedOption, setSelectedOption] = React.useState<Option<T> | undefined>(undefined);
	const [selectedOptionsMap, setSelectedOptionsMap] = React.useState<Map<T, Option<T>>>(new Map());

	const triggerRef = React.useRef<HTMLDivElement>(null);
	const searchInputRef = React.useRef<HTMLInputElement>(null);
	const [triggerWidth, setTriggerWidth] = React.useState<number | undefined>();

	const hasError: boolean = !!error || !!params.errored;
	const isDisabled: boolean = !!params.disabled || !!contextDisabled;

	const debouncedSearchValue = useDebounce(searchValue, 300);
	const selectionVariant = params.selectionVariant || "select";

	// Determine which options source to use: internal state (for async fetching) or external props (for static options)
	const options = React.useMemo(
		() => (params.fetchOptions ? internalOptions : params.options || []),
		[params.fetchOptions, internalOptions, params.options]
	);

	const singleValue = params.mode === "single" ? params.value : undefined;
	const multipleValue = params.mode === "multiple" ? params.value : undefined;

	// Effect Event to process fetched options with the latest selected options state.
	// This reads selectedOptionsMap and multipleValue without making them reactive dependencies,
	// preventing unnecessary re-fetches while ensuring we always use the most current values
	// when the async fetch completes.
	const onFetchedOptions = React.useEffectEvent((fetchedOptions: Option<T>[]) => {
		if (params.mode === "multiple" && multipleValue) {
			const selectedValues = new Set(multipleValue);
			const fetchedValues = new Set(fetchedOptions.map((opt) => opt.value));

			// Preserve selected options that aren't in the current fetch results
			const missingSelectedOptions = Array.from(selectedOptionsMap.values()).filter(
				(opt) => selectedValues.has(opt.value) && !fetchedValues.has(opt.value)
			);

			setInternalOptions([...missingSelectedOptions, ...fetchedOptions]);
		} else {
			setInternalOptions(fetchedOptions);
		}
	});

	// Fetch options asynchronously when using fetchOptions. Debounces search input to reduce API calls.
	// Re-runs only when search term or fetch function changes, not when selected values change.
	React.useEffect(() => {
		if (!params.fetchOptions) return;

		const loadOptions = async () => {
			setIsLoading(true);
			try {
				const fetchedOptions = await params.fetchOptions!(debouncedSearchValue);
				onFetchedOptions(fetchedOptions);
			} catch (error) {
				console.error("Error fetching options:", error);
				setInternalOptions([]);
			} finally {
				setIsLoading(false);
			}
		};

		loadOptions();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [debouncedSearchValue, params.fetchOptions]);

	// Sync selectedOption state for single mode when value prop changes. If the value isn't found in options,
	// creates a synthetic option from the value itself (fallback for async-loaded or filtered-out options).
	React.useEffect(() => {
		if (params.mode === "single") {
			if (singleValue === null || singleValue === undefined) {
				setSelectedOption(undefined);
				return;
			}

			if (selectedOption?.value === singleValue) {
				return;
			}

			const foundOption = options.find((option) => option.value === singleValue);
			if (foundOption) {
				setSelectedOption(foundOption);
			} else {
				setSelectedOption({
					label:
						typeof singleValue === "object" && singleValue !== null && "name" in singleValue
							? String((singleValue as { name: unknown }).name)
							: String(singleValue),
					value: singleValue,
				});
			}
		}
	}, [params.mode, singleValue, selectedOption, options]);

	// Sync selectedOptionsMap for multiple mode when value prop changes. Maintains a map of selected values
	// to their option objects, preserving previously selected options even if they're not in current options
	// (useful for async-loaded options that may be filtered out). Creates synthetic options as fallback.
	React.useEffect(() => {
		if (params.mode === "multiple" && multipleValue) {
			const newMap = new Map<T, Option<T>>();

			multipleValue.forEach((val) => {
				const optionInList = options.find((option) => option.value === val);
				if (optionInList) {
					newMap.set(val, optionInList);
				} else if (selectedOptionsMap.has(val)) {
					newMap.set(val, selectedOptionsMap.get(val)!);
				} else {
					newMap.set(val, {
						label:
							typeof val === "object" && val !== null && "name" in val
								? String((val as { name: unknown }).name)
								: String(val),
						value: val,
					});
				}
			});

			setSelectedOptionsMap(newMap);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [params.mode, multipleValue, options]);

	// Filter options for single mode. Returns all options if not in combobox mode or no search term.
	// When using fetchOptions, filtering is handled server-side, so we return options as-is.
	// Otherwise, filters client-side by matching search term against option labels or values.
	const singleFilteredOptions = React.useMemo(() => {
		if (params.mode !== "single") return [];
		if (selectionVariant !== "combobox" || !searchValue.trim()) {
			return options;
		}
		if (params.fetchOptions) {
			return options;
		}
		return options.filter((option) => {
			const searchText = searchValue.toLowerCase();
			const optionText =
				typeof option.label === "string" ? option.label.toLowerCase() : String(option.value).toLowerCase();
			return optionText.includes(searchText);
		});
	}, [params.mode, options, searchValue, selectionVariant, params.fetchOptions]);

	// Convert selected values array to Option objects using the selectedOptionsMap. Preserves the order
	// of values as specified in the value prop, which is important for ordered selection components.
	const selectedOptions = React.useMemo(() => {
		if (params.mode !== "multiple" || !multipleValue) return [];
		return multipleValue.map((val) => selectedOptionsMap.get(val)).filter(Boolean) as Option<T>[];
	}, [params.mode, multipleValue, selectedOptionsMap]);

	// Filter out already-selected options from available options for multiple mode. Uses ID comparison
	// when values are objects with an 'id' property, otherwise uses value equality. This prevents
	// showing selected options in the dropdown again.
	const availableOptions = React.useMemo(() => {
		if (params.mode !== "multiple" || !multipleValue) return [];
		const selectedIds = new Set(
			multipleValue.map((val) =>
				typeof val === "object" && val !== null && "id" in val ? (val as { id: string }).id : val
			)
		);
		return options.filter((option) => {
			const optionId =
				typeof option.value === "object" && option.value !== null && "id" in option.value
					? (option.value as { id: string }).id
					: option.value;
			return !selectedIds.has(optionId);
		});
	}, [params.mode, options, multipleValue]);

	// Filter available options for multiple mode. Returns all available options if not in combobox mode
	// or no search term. When using fetchOptions, filtering is handled server-side, so we return
	// availableOptions as-is. Otherwise, filters client-side by matching search term.
	const multipleFilteredOptions = React.useMemo(() => {
		if (params.mode !== "multiple") return [];
		if (selectionVariant !== "combobox" || !searchValue.trim()) {
			return availableOptions;
		}
		if (params.fetchOptions) {
			return availableOptions;
		}
		return availableOptions.filter((option) => {
			const searchText = searchValue.toLowerCase();
			const optionText =
				typeof option.label === "string" ? option.label.toLowerCase() : String(option.value).toLowerCase();
			return optionText.includes(searchText);
		});
	}, [params.mode, availableOptions, searchValue, selectionVariant, params.fetchOptions]);

	const filteredOptions = params.mode === "single" ? singleFilteredOptions : multipleFilteredOptions;

	const virtualizationResult = useVirtualizeHook({
		items: filteredOptions,
		itemHeight: params.virtualizationConfig?.itemHeight ?? 32,
		maxHeight: params.virtualizationConfig?.maxHeight ?? 240,
		enabled: true,
		autoForceMeasure: true,
		isOpen: isOpen,
		onItemSelect: (option: Option<T>) => {
			if (!option.disabled && !isDisabled) {
				if (params.mode === "single") {
					params.onChange(option.value);
					setIsOpen(false);
					setSearchValue("");
				} else {
					const newValue = [...multipleValue!, option.value];
					params.onChange(newValue);
				}
			}
		},
	});

	const {
		parentRef,
		virtualizer,
		containerHeight,
		focusedIndex,
		isKeyboardNavActive,
		handleKeyDown,
		handleItemMouseEnter,
		handleItemMouseLeave,
	} = virtualizationResult;

	const handleOpenChange = (open: boolean) => {
		setIsOpen(open);
		if (open && triggerRef.current) {
			setTriggerWidth(triggerRef.current.offsetWidth);
			setTimeout(() => {
				if (selectionVariant === "combobox" && searchInputRef.current) {
					searchInputRef.current.focus();
				} else if (parentRef.current) {
					parentRef.current.focus();
				}
			}, 0);
		} else if (!open) {
			setSearchValue("");
			params.onSearchChange?.("");
		}
	};

	const handleItemSelect = (option: Option<T>) => {
		if (!option.disabled && !isDisabled) {
			if (params.mode === "single") {
				params.onChange(option.value);
				setIsOpen(false);
				setSearchValue("");
			} else {
				const newValue = [...multipleValue!, option.value];
				params.onChange(newValue);
			}
		}
	};

	if (params.mode === "single") {
		return {
			mode: "single",
			inputId,
			hasError,
			isDisabled,
			isOpen,
			searchValue,
			isLoading,
			selectedOption,
			filteredOptions,
			options,
			triggerWidth,
			triggerRef,
			searchInputRef,
			parentRef,
			virtualizer,
			containerHeight,
			focusedIndex,
			isKeyboardNavActive,
			handleKeyDown,
			handleItemMouseEnter,
			handleItemMouseLeave,
			setIsOpen,
			setSearchValue,
			handleOpenChange,
			handleItemSelect,
		} as UseSelectBaseReturnSingle<T>;
	} else {
		return {
			mode: "multiple",
			inputId,
			hasError,
			isDisabled,
			isOpen,
			searchValue,
			isLoading,
			selectedOptions,
			availableOptions,
			filteredOptions,
			options,
			triggerWidth,
			triggerRef,
			searchInputRef,
			parentRef,
			virtualizer,
			containerHeight,
			focusedIndex,
			isKeyboardNavActive,
			handleKeyDown,
			handleItemMouseEnter,
			handleItemMouseLeave,
			setIsOpen,
			setSearchValue,
			handleOpenChange,
			handleItemSelect,
		} as UseSelectBaseReturnMultiple<T>;
	}
};
